
(() => {
    const HEADER_SELECTOR = '.wambolt-header-shell';
    const NAV_SELECTOR = '.wambolt-mobile-nav[data-navigation-status]';
    const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
    const SCROLL_THRESHOLD = 10;
    const SCROLL_TOP_THRESHOLD = 50;
    const NAV_CLOSE_DURATION = 620;
    const DESKTOP_OPEN_DELAY = 120;
    const DESKTOP_CLOSE_DELAY = 150;
    const DESKTOP_HIDE_DURATION = 350;
    const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');
    const HOVER_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');
    const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* PRE-ORDER CTA STATE */

    function initPreorderCTA(header) {
        if (!header) return;
        if (header.dataset.wamboltPreorderCtaInit === 'true') return;

        const ctaLinks = Array.from(header.querySelectorAll('[data-wambolt-preorder-cta]'));
        const ctaText = Array.from(header.querySelectorAll('[data-wambolt-preorder-cta-text]'));

        if (!ctaLinks.length && !ctaText.length) return;
        header.dataset.wamboltPreorderCtaInit = 'true';

        const reserveLabel = header.dataset.wamboltPreorderReserveLabel || 'Reserve Now';
        const reserveUrl = header.dataset.wamboltPreorderReserveUrl || '';
        const cartUrl = header.dataset.wamboltPreorderCartUrl || '/cart';

        function applyState(count) {
            const parsedCount = Number(count);
            const safeCount = Number.isFinite(parsedCount) ? Math.max(0, parsedCount) : 0;
            const hasPreorder = safeCount > 0;
            const label = hasPreorder ? `Pre-Order (${safeCount})` : reserveLabel;
            const url = hasPreorder ? cartUrl : reserveUrl;

            ctaText.forEach(element => {
                element.textContent = label;
            });

            ctaLinks.forEach(link => {
                if (url) {
                    link.setAttribute('href', url);
                } else {
                    link.removeAttribute('href');
                }
                if (link.hasAttribute('aria-label')) {
                    link.setAttribute('aria-label', label);
                }
            });

            header.dataset.wamboltPreorderCartCount = String(safeCount);
        }

        function handleCartUpdate(event) {
            const promise = event?.promise;
            if (!promise || typeof promise.then !== 'function') return;

            promise.then(({ cart, detail }) => {
                const count = cart?.totalQuantity ?? detail?.itemCount;
                if (!Number.isFinite(Number(count))) return;
                applyState(Number(count));
            }).catch(error => {
                if (error?.name !== 'AbortError') {
                    console.warn('[wambolt-header] Cart update rejected:', error);
                }
            });
        }

        applyState(Number(header.dataset.wamboltPreorderCartCount || 0));

        document.addEventListener('shopify:cart:lines-update', handleCartUpdate);

        header._wamboltPreorderCtaCleanup = () => {
            document.removeEventListener('shopify:cart:lines-update', handleCartUpdate);
            delete header.dataset.wamboltPreorderCtaInit;
        };
    }

    /* HEADER SCROLL VISIBILITY */

    function initHeaderScrollVisibility(header) {
        if (!header) return;
        if (header.dataset.wamboltScrollInit === 'true') return;

        header.dataset.wamboltScrollInit = 'true';

        const pageWrapper = document.querySelector(PAGE_WRAPPER_SELECTOR);
        let frameId = null;

        function getWindowScrollTop() {
            return Math.max(window.scrollY || 0, 0);
        }

        function getWrapperScrollTop() {
            if (!pageWrapper) return 0;
            return Math.max(pageWrapper.scrollTop || 0, 0);
        }

        function getInitialScrollTop() {
            return Math.max(getWindowScrollTop(), getWrapperScrollTop());
        }

        let lastScrollTop = getInitialScrollTop();
        let latestScrollTop = lastScrollTop;
        let currentDirection = 'up';
        let currentStarted = lastScrollTop > SCROLL_TOP_THRESHOLD ? 'true' : 'false';
        let appliedDirection = null;
        let appliedStarted = null;

        function applyState() {
            const directionChanged = currentDirection !== appliedDirection;
            const startedChanged = currentStarted !== appliedStarted;
            if (!directionChanged && !startedChanged) return;

            if (directionChanged) {
                header.setAttribute('data-scrolling-direction', currentDirection);
                appliedDirection = currentDirection;
            }

            if (startedChanged) {
                header.setAttribute('data-scrolling-started', currentStarted);
                appliedStarted = currentStarted;
            }

            if (
                DESKTOP_QUERY.matches &&
                currentStarted === 'true' &&
                currentDirection === 'down' &&
                typeof header._wamboltCloseDesktopMega === 'function'
            ) {
                header._wamboltCloseDesktopMega();
            }
        }

        function updateScrollState() {
            frameId = null;
            const currentScrollTop = Math.max(latestScrollTop, 0);

            if (currentScrollTop <= SCROLL_TOP_THRESHOLD) {
                currentStarted = 'false';
                currentDirection = 'up';
                lastScrollTop = currentScrollTop;
                applyState();
                return;
            }

            currentStarted = 'true';
            const delta = currentScrollTop - lastScrollTop;

            if (Math.abs(delta) >= SCROLL_THRESHOLD) {
                currentDirection = delta > 0 ? 'down' : 'up';
                lastScrollTop = currentScrollTop;
            }

            applyState();
        }

        function queueScrollUpdate(scrollTop) {
            latestScrollTop = Math.max(scrollTop, 0);
            if (frameId !== null) return;
            frameId = window.requestAnimationFrame(updateScrollState);
        }

        function handleWindowScroll() {
            queueScrollUpdate(getWindowScrollTop());
        }

        function handlePageWrapperScroll() {
            queueScrollUpdate(getWrapperScrollTop());
        }

        applyState();

        window.addEventListener('scroll', handleWindowScroll, { passive: true });

        if (pageWrapper) {
            pageWrapper.addEventListener('scroll', handlePageWrapperScroll, { passive: true });
        }

        header._wamboltScrollCleanup = () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
                frameId = null;
            }

            window.removeEventListener('scroll', handleWindowScroll);

            if (pageWrapper) {
                pageWrapper.removeEventListener('scroll', handlePageWrapperScroll);
            }

            header.setAttribute('data-scrolling-started', 'false');
            header.setAttribute('data-scrolling-direction', 'up');
            delete header.dataset.wamboltScrollInit;
        };
    }

    /* DESKTOP MEGA NAVIGATION */

    function initDesktopMega(header) {
        if (!header) return;
        if (header.dataset.wamboltDesktopMegaInit === 'true') return;

        const mega = header.querySelector('[data-wambolt-desktop-mega]');
        const triggers = Array.from(header.querySelectorAll('[data-wambolt-desktop-dropdown-toggle]'));

        if (!mega || !triggers.length) return;

        header.dataset.wamboltDesktopMegaInit = 'true';

        const megaShell = mega.querySelector('.wambolt-header__mega-shell');
        const closeButton = mega.querySelector('[data-wambolt-desktop-dropdown-close]');
        const panels = Array.from(mega.querySelectorAll('[data-wambolt-desktop-dropdown-panel]'));
        const navigationLinks = Array.from(header.querySelectorAll('.wambolt-header--desktop a[href]'));
        const megaLinks = Array.from(mega.querySelectorAll('a[href]'));

        let activeIndex = null;
        let activeTrigger = null;
        let activePanel = null;
        let openTimer = null;
        let closeTimer = null;
        let hideTimer = null;
        let switchTimeline = null;

        /* DIRECTIONAL PANEL ANIMATION */

        function getGSAP() {
            return window.gsap || null;
        }

        function getPanelColumns(panel) {
            if (!panel) return [];
            return Array.from(panel.querySelectorAll('[data-wambolt-desktop-dropdown-column]'));
        }

        function measurePanelHeight(panel) {
            if (!panel) return 0;

            const wasHidden = panel.hidden;
            const previousStyle = panel.getAttribute('style');

            panel.hidden = false;
            panel.style.position = 'absolute';
            panel.style.top = '0';
            panel.style.left = '0';
            panel.style.right = '0';
            panel.style.width = '100%';
            panel.style.visibility = 'hidden';
            panel.style.pointerEvents = 'none';

            const height = panel.getBoundingClientRect().height;

            if (previousStyle === null) {
                panel.removeAttribute('style');
            } else {
                panel.setAttribute('style', previousStyle);
            }

            panel.hidden = wasHidden;
            return height;
        }

        function clearPanelAnimationStyles(panel) {
            if (!panel) return;

            const gsap = getGSAP();
            const columns = getPanelColumns(panel);
            const inner = panel.querySelector('.wambolt-header__mega-inner');

            if (gsap && columns.length) {
                gsap.killTweensOf(columns);
                gsap.set(columns, {
                    clearProps: 'transform,opacity,visibility'
                });
            } else {
                columns.forEach(column => {
                    column.style.removeProperty('transform');
                    column.style.removeProperty('opacity');
                    column.style.removeProperty('visibility');
                });
            }

            panel.style.removeProperty('position');
            panel.style.removeProperty('top');
            panel.style.removeProperty('left');
            panel.style.removeProperty('right');
            panel.style.removeProperty('width');
            panel.style.removeProperty('z-index');
            panel.style.removeProperty('pointer-events');

            if (inner) {
                inner.style.removeProperty('background');
                inner.style.removeProperty('border-color');
                inner.style.removeProperty('box-shadow');
            }
        }

        function finishDesktopSwitch() {
            if (switchTimeline) {
                switchTimeline.kill();
                switchTimeline = null;
            }

            const gsap = getGSAP();

            panels.forEach(panel => {
                if (gsap) {
                    gsap.killTweensOf(getPanelColumns(panel));
                }

                clearPanelAnimationStyles(panel);

                const isCurrent = panel === activePanel;
                panel.hidden = !isCurrent;
                panel.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
                panel.toggleAttribute('inert', !isCurrent);
            });

            if (megaShell) {
                if (gsap) {
                    gsap.killTweensOf(megaShell);
                }
                megaShell.style.removeProperty('height');
            }
        }

        function animatePanelSwitch(index, trigger, panel) {
            const gsap = getGSAP();
            const fromPanel = activePanel;
            const fromIndex = Number(activeIndex);
            const toIndex = Number(index);

            if (!fromPanel || !gsap || REDUCED_MOTION_QUERY.matches) {
                resetTriggers(trigger);
                resetPanels(panel);

                activeIndex = String(index);
                activeTrigger = trigger;
                activePanel = panel;

                trigger.setAttribute('aria-expanded', 'true');
                panel.hidden = false;
                panel.setAttribute('aria-hidden', 'false');
                panel.removeAttribute('inert');
                mega.setAttribute('aria-hidden', 'false');
                return;
            }

            finishDesktopSwitch();

            const direction = toIndex > fromIndex ? 1 : -1;
            const outgoingX = direction * -42;
            const incomingX = direction * 42;
            const fromColumns = getPanelColumns(fromPanel);
            const toColumns = getPanelColumns(panel);
            const fromHeight = fromPanel.getBoundingClientRect().height;
            const toHeight = measurePanelHeight(panel);
            const toInner = panel.querySelector('.wambolt-header__mega-inner');

            resetTriggers(trigger);
            trigger.setAttribute('aria-expanded', 'true');

            activeIndex = String(index);
            activeTrigger = trigger;
            activePanel = panel;

            mega.setAttribute('aria-hidden', 'false');

            panel.hidden = false;
            panel.setAttribute('aria-hidden', 'false');
            panel.removeAttribute('inert');

            panel.style.position = 'absolute';
            panel.style.top = '0';
            panel.style.left = '0';
            panel.style.right = '0';
            panel.style.width = '100%';
            panel.style.zIndex = '2';
            panel.style.pointerEvents = 'none';

            fromPanel.style.zIndex = '1';

            if (toInner) {
                toInner.style.background = 'transparent';
                toInner.style.borderColor = 'transparent';
                toInner.style.boxShadow = 'none';
            }

            gsap.set(fromColumns, { x: 0, autoAlpha: 1 });
            gsap.set(toColumns, { x: incomingX, autoAlpha: 0 });

            if (megaShell) {
                gsap.set(megaShell, { height: fromHeight });
            }

            switchTimeline = gsap.timeline({
                defaults: { overwrite: 'auto' },
                onComplete() {
                    fromPanel.hidden = true;
                    fromPanel.setAttribute('aria-hidden', 'true');
                    fromPanel.setAttribute('inert', '');

                    clearPanelAnimationStyles(fromPanel);
                    clearPanelAnimationStyles(panel);

                    if (megaShell) {
                        megaShell.style.removeProperty('height');
                    }

                    panel.hidden = false;
                    panel.setAttribute('aria-hidden', 'false');
                    panel.removeAttribute('inert');
                    switchTimeline = null;
                }
            });

            switchTimeline.to(fromColumns, {
                x: outgoingX,
                autoAlpha: 0,
                duration: 0.18,
                stagger: {
                    each: 0.025,
                    from: direction > 0 ? 'start' : 'end'
                },
                ease: 'power2.in'
            }, 0);

            if (megaShell && toHeight) {
                switchTimeline.to(megaShell, {
                    height: toHeight,
                    duration: 0.34,
                    ease: 'power3.inOut'
                }, 0);
            }

            switchTimeline.to(toColumns, {
                x: 0,
                autoAlpha: 1,
                duration: 0.32,
                stagger: {
                    each: 0.045,
                    from: direction > 0 ? 'start' : 'end'
                },
                ease: 'power3.out'
            }, 0.11);
        }

        /* TIMER HELPERS */

        function clearOpenTimer() {
            if (!openTimer) return;
            window.clearTimeout(openTimer);
            openTimer = null;
        }

        function clearCloseTimer() {
            if (!closeTimer) return;
            window.clearTimeout(closeTimer);
            closeTimer = null;
        }

        function clearHideTimer() {
            if (!hideTimer) return;
            window.clearTimeout(hideTimer);
            hideTimer = null;
        }

        function clearTimers() {
            clearOpenTimer();
            clearCloseTimer();
            clearHideTimer();
        }

        /* PANEL HELPERS */

        function getPanel(index) {
            return panels.find(panel =>
                panel.getAttribute('data-wambolt-desktop-dropdown-panel') === String(index)
            );
        }

        function getTrigger(index) {
            return triggers.find(trigger =>
                trigger.getAttribute('data-wambolt-desktop-dropdown-toggle') === String(index)
            );
        }

        function resetPanels(exceptPanel = null) {
            panels.forEach(panel => {
                if (panel === exceptPanel) return;
                panel.hidden = true;
                panel.setAttribute('aria-hidden', 'true');
                panel.setAttribute('inert', '');
            });
        }

        function resetTriggers(exceptTrigger = null) {
            triggers.forEach(trigger => {
                if (trigger === exceptTrigger) return;
                trigger.setAttribute('aria-expanded', 'false');
            });
        }

        /* OPEN DESKTOP MENU */

        function openDropdown(index) {
            if (!DESKTOP_QUERY.matches) return;

            clearTimers();

            const trigger = getTrigger(index);
            const panel = getPanel(index);

            if (!trigger || !panel) return;

            const megaIsOpen = mega.getAttribute('aria-hidden') === 'false';
            const isCurrent = activeIndex === String(index) && megaIsOpen;

            if (isCurrent) return;

            if (megaIsOpen && activePanel) {
                animatePanelSwitch(index, trigger, panel);
                return;
            }

            resetTriggers(trigger);
            resetPanels(panel);

            activeIndex = String(index);
            activeTrigger = trigger;
            activePanel = panel;

            trigger.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
            panel.setAttribute('aria-hidden', 'false');
            panel.removeAttribute('inert');
            mega.setAttribute('aria-hidden', 'false');

            const gsap = getGSAP();

            if (gsap && !REDUCED_MOTION_QUERY.matches) {
                const columns = getPanelColumns(panel);
                gsap.killTweensOf(columns);

                gsap.fromTo(columns, {
                    y: 10,
                    autoAlpha: 0
                }, {
                    y: 0,
                    autoAlpha: 1,
                    duration: 0.34,
                    stagger: 0.045,
                    ease: 'power3.out',
                    clearProps: 'transform,opacity,visibility'
                });
            }
        }

        /* CLOSE DESKTOP MENU */

        function closeDropdown(returnFocus = false) {
            if (mega.getAttribute('aria-hidden') === 'true') {
                clearOpenTimer();
                clearCloseTimer();
                return;
            }

            clearOpenTimer();
            clearCloseTimer();
            clearHideTimer();
            finishDesktopSwitch();

            if (mega.getAttribute('aria-hidden') === 'true') return;

            const triggerToFocus = activeTrigger;
            mega.setAttribute('aria-hidden', 'true');
            resetTriggers();

            activeIndex = null;
            activeTrigger = null;

            const panelToHide = activePanel;
            activePanel = null;

            const finishClose = () => {
                if (panelToHide) {
                    panelToHide.hidden = true;
                    panelToHide.setAttribute('aria-hidden', 'true');
                    panelToHide.setAttribute('inert', '');
                }
                resetPanels();
                hideTimer = null;
            };

            if (REDUCED_MOTION_QUERY.matches) {
                finishClose();
            } else {
                hideTimer = window.setTimeout(finishClose, DESKTOP_HIDE_DURATION);
            }

            if (returnFocus && triggerToFocus) {
                triggerToFocus.focus();
            }
        }

        header._wamboltCloseDesktopMega = () => {
            closeDropdown(false);
        };

        /* HOVER INTENT */

        function scheduleOpen(index) {
            if (!DESKTOP_QUERY.matches || !HOVER_QUERY.matches) return;

            clearCloseTimer();
            clearOpenTimer();

            if (activeIndex === String(index)) return;

            openTimer = window.setTimeout(() => {
                openDropdown(index);
                openTimer = null;
            }, DESKTOP_OPEN_DELAY);
        }

        function scheduleClose() {
            if (!DESKTOP_QUERY.matches || !HOVER_QUERY.matches) return;

            clearOpenTimer();
            clearCloseTimer();

            closeTimer = window.setTimeout(() => {
                closeDropdown(false);
                closeTimer = null;
            }, DESKTOP_CLOSE_DELAY);
        }

        function cancelClose() {
            clearCloseTimer();
        }

        /* DESKTOP TRIGGER EVENTS */

        const triggerHandlers = new Map();

        triggers.forEach(trigger => {
            const index = trigger.getAttribute('data-wambolt-desktop-dropdown-toggle');

            const handlePointerEnter = () => {
                scheduleOpen(index);
            };

            const handlePointerLeave = () => {
                scheduleClose();
            };

            const handleClick = event => {
                // Learn remains clickable while retaining hover functionality.
                if (trigger.hasAttribute('data-wambolt-desktop-dropdown-link')) {
                    clearTimers();
                    closeDropdown(false);
                    return;
                }

                event.preventDefault();
                clearTimers();

                const isCurrent =
                    activeIndex === String(index) &&
                    mega.getAttribute('aria-hidden') === 'false';

                if (isCurrent) {
                    closeDropdown(false);
                    return;
                }

                openDropdown(index);
            };

            const handleFocus = () => {
                if (!DESKTOP_QUERY.matches) return;
                clearTimers();
                openDropdown(index);
            };

            const handleKeydown = event => {
                if (!DESKTOP_QUERY.matches) return;

                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    openDropdown(index);

                    const panel = getPanel(index);

                    window.requestAnimationFrame(() => {
                        const firstFocusable = panel?.querySelector(
                            'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
                        );
                        if (firstFocusable) firstFocusable.focus();
                    });
                    return;
                }

                if (event.key === 'Escape') {
                    event.preventDefault();
                    closeDropdown(true);
                }
            };

            trigger.addEventListener('pointerenter', handlePointerEnter);
            trigger.addEventListener('pointerleave', handlePointerLeave);
            trigger.addEventListener('click', handleClick);
            trigger.addEventListener('focus', handleFocus);
            trigger.addEventListener('keydown', handleKeydown);

            triggerHandlers.set(trigger, {
                handlePointerEnter,
                handlePointerLeave,
                handleClick,
                handleFocus,
                handleKeydown
            });
        });

        /* MEGA SHELL HOVER */

        function handleMegaPointerEnter() {
            cancelClose();
        }

        function handleMegaPointerLeave() {
            scheduleClose();
        }

        if (megaShell) {
            megaShell.addEventListener('pointerenter', handleMegaPointerEnter);
            megaShell.addEventListener('pointerleave', handleMegaPointerLeave);
        }

        /* BACKDROP */

        function handleBackdropClick() {
            closeDropdown(false);
        }

        if (closeButton) {
            closeButton.addEventListener('click', handleBackdropClick);
        }

        /* LINK NAVIGATION */

        function handleNavigationLinkClick() {
            closeDropdown(false);
        }

        navigationLinks.forEach(link => {
            link.addEventListener('click', handleNavigationLinkClick);
        });

        megaLinks.forEach(link => {
            link.addEventListener('click', handleNavigationLinkClick);
        });

        /* DOCUMENT KEYBOARD */

        function handleDocumentKeydown(event) {
            if (
                event.key !== 'Escape' ||
                mega.getAttribute('aria-hidden') === 'true'
            ) return;

            event.preventDefault();
            closeDropdown(true);
        }

        document.addEventListener('keydown', handleDocumentKeydown);

        /* FOCUS LEAVES MENU */

        function handleDocumentFocusIn(event) {
            if (mega.getAttribute('aria-hidden') === 'true') return;

            const target = event.target;
            if (!(target instanceof Element)) return;

            const insideMega = megaShell && megaShell.contains(target);
            const insideActiveTrigger = activeTrigger &&
                (target === activeTrigger || activeTrigger.contains(target));

            if (insideMega || insideActiveTrigger) return;
            closeDropdown(false);
        }

        document.addEventListener('focusin', handleDocumentFocusIn);

        /* BREAKPOINT RESET */

        function handleDesktopBreakpoint(event) {
            if (event.matches) return;
            closeDropdown(false);
        }

        if (typeof DESKTOP_QUERY.addEventListener === 'function') {
            DESKTOP_QUERY.addEventListener('change', handleDesktopBreakpoint);
        } else if (typeof DESKTOP_QUERY.addListener === 'function') {
            DESKTOP_QUERY.addListener(handleDesktopBreakpoint);
        }

        mega.setAttribute('aria-hidden', 'true');
        resetTriggers();
        resetPanels();

        /* DESKTOP CLEANUP */

        header._wamboltDesktopMegaCleanup = () => {
            clearTimers();
            finishDesktopSwitch();

            triggerHandlers.forEach((handlers, trigger) => {
                trigger.removeEventListener('pointerenter', handlers.handlePointerEnter);
                trigger.removeEventListener('pointerleave', handlers.handlePointerLeave);
                trigger.removeEventListener('click', handlers.handleClick);
                trigger.removeEventListener('focus', handlers.handleFocus);
                trigger.removeEventListener('keydown', handlers.handleKeydown);
            });

            if (megaShell) {
                megaShell.removeEventListener('pointerenter', handleMegaPointerEnter);
                megaShell.removeEventListener('pointerleave', handleMegaPointerLeave);
            }

            if (closeButton) {
                closeButton.removeEventListener('click', handleBackdropClick);
            }

            navigationLinks.forEach(link => {
                link.removeEventListener('click', handleNavigationLinkClick);
            });

            megaLinks.forEach(link => {
                link.removeEventListener('click', handleNavigationLinkClick);
            });

            document.removeEventListener('keydown', handleDocumentKeydown);
            document.removeEventListener('focusin', handleDocumentFocusIn);

            if (typeof DESKTOP_QUERY.removeEventListener === 'function') {
                DESKTOP_QUERY.removeEventListener('change', handleDesktopBreakpoint);
            } else if (typeof DESKTOP_QUERY.removeListener === 'function') {
                DESKTOP_QUERY.removeListener(handleDesktopBreakpoint);
            }

            mega.setAttribute('aria-hidden', 'true');
            resetTriggers();
            resetPanels();

            delete header._wamboltCloseDesktopMega;
            delete header.dataset.wamboltDesktopMegaInit;
        };
    }

    /* MOBILE NAVIGATION */

    function initNavigation(nav) {
        if (!nav) return;
        if (nav.dataset.wamboltNavigationInit === 'true') return;

        nav.dataset.wamboltNavigationInit = 'true';

        const toggle = nav.querySelector('[data-navigation-toggle="toggle"]');
        const closeButtons = nav.querySelectorAll('[data-navigation-toggle="close"]');
        const content = nav.querySelector('.wambolt-mobile-nav__content');
        const inner = nav.querySelector('.wambolt-mobile-nav__inner');
        const items = nav.querySelectorAll('[data-navigation-item]');
        const navigationLinks = nav.querySelectorAll('a[href]');
        const dropdownItems = Array.from(nav.querySelectorAll('[data-wambolt-mobile-dropdown-item]'));
        const dropdownToggles = Array.from(nav.querySelectorAll('[data-wambolt-mobile-dropdown-toggle]'));
        const subdropdownItems = Array.from(nav.querySelectorAll('[data-wambolt-mobile-subdropdown-item]'));
        const subdropdownToggles = Array.from(nav.querySelectorAll('[data-wambolt-mobile-subdropdown-toggle]'));

        let unlockTimer = null;
        let closeResetTimer = null;
        let measureFrame = null;
        let lenisStoppedByNavigation = false;
        let previousDocumentOverflow = null;

        /* ITEM STAGGER */

        items.forEach((item, index) => {
            const delay = index * 0.05;
            item.style.transitionDelay = `${delay}s`;

            const linkText = item.querySelector('.wambolt-mobile-nav__link-text');
            if (linkText) {
                linkText.style.transitionDelay = `${delay}s`;
            }
        });

        /* MOBILE STATE */

        function isOpen() {
            return nav.getAttribute('data-navigation-status') === 'active';
        }

        function setNavigationState(open) {
            nav.setAttribute(
                'data-navigation-status',
                open ? 'active' : 'not-active'
            );

            if (toggle) {
                toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            }

            if (content) {
                content.setAttribute('aria-hidden', open ? 'false' : 'true');
                content.toggleAttribute('inert', !open);
            }
        }

        /* CONTENT HEIGHT */

        function measureContent() {
            if (!content) return;
            content.style.setProperty(
                '--wambolt-mobile-nav-content-height',
                `${content.scrollHeight}px`
            );
        }

        function queueMeasureContent() {
            if (measureFrame !== null) {
                window.cancelAnimationFrame(measureFrame);
            }

            measureFrame = window.requestAnimationFrame(() => {
                measureFrame = null;
                measureContent();
            });
        }

        /* MOBILE DROPDOWN HELPERS */

        function getMobileGSAP() {
            return window.gsap || null;
        }

        function clearMobilePanelStyles(panel) {
            if (!panel) return;

            const gsap = getMobileGSAP();

            if (gsap) {
                gsap.killTweensOf(panel);
                gsap.set(panel, {
                    clearProps: 'height,opacity,visibility'
                });
            } else {
                panel.style.removeProperty('height');
                panel.style.removeProperty('opacity');
                panel.style.removeProperty('visibility');
            }
        }

        function setMobilePanelImmediate(panel, open) {
            if (!panel) return;

            clearMobilePanelStyles(panel);
            panel.hidden = !open;
            panel.toggleAttribute('inert', !open);
        }

        function animateMobilePanel(panel, open, onComplete = null) {
            if (!panel) {
                if (onComplete) onComplete();
                return;
            }

            const gsap = getMobileGSAP();

            if (!gsap || REDUCED_MOTION_QUERY.matches) {
                setMobilePanelImmediate(panel, open);
                queueMeasureContent();
                if (onComplete) onComplete();
                return;
            }

            if (open) {
                const wasHidden = panel.hidden;
                const startHeight = wasHidden
                    ? 0
                    : panel.getBoundingClientRect().height;

                let startOpacity = 1;

                if (!wasHidden) {
                    const currentOpacity = Number(gsap.getProperty(panel, 'opacity'));
                    if (Number.isFinite(currentOpacity)) {
                        startOpacity = currentOpacity;
                    }
                } else {
                    startOpacity = 0;
                }

                panel.hidden = false;
                panel.removeAttribute('inert');

                const targetHeight = panel.scrollHeight;
                gsap.killTweensOf(panel);

                gsap.set(panel, {
                    height: startHeight,
                    opacity: startOpacity
                });

                queueMeasureContent();

                gsap.to(panel, {
                    height: targetHeight,
                    opacity: 1,
                    duration: 0.32,
                    ease: 'power3.out',
                    overwrite: 'auto',
                    onComplete() {
                        gsap.set(panel, {
                            clearProps: 'height,opacity,visibility'
                        });
                        queueMeasureContent();
                        if (onComplete) onComplete();
                    }
                });
                return;
            }

            if (panel.hidden) {
                panel.setAttribute('inert', '');
                if (onComplete) onComplete();
                return;
            }

            const currentHeight = panel.getBoundingClientRect().height;
            panel.setAttribute('inert', '');
            gsap.killTweensOf(panel);
            gsap.set(panel, { height: currentHeight });

            gsap.to(panel, {
                height: 0,
                opacity: 0,
                duration: 0.26,
                ease: 'power2.inOut',
                overwrite: 'auto',
                onComplete() {
                    panel.hidden = true;
                    gsap.set(panel, {
                        clearProps: 'height,opacity,visibility'
                    });
                    queueMeasureContent();
                    if (onComplete) onComplete();
                }
            });
        }

        /* MOBILE SUBDROPDOWNS */

        function closeSubdropdown(item, restoreFocus = false, animate = true) {
            if (!item) return;

            const subToggle = item.querySelector('[data-wambolt-mobile-subdropdown-toggle]');
            const panel = item.querySelector('[data-wambolt-mobile-subdropdown-panel]');

            item.setAttribute('data-open', 'false');

            if (subToggle) {
                subToggle.setAttribute('aria-expanded', 'false');
            }

            const finishClose = () => {
                if (restoreFocus && subToggle) subToggle.focus();
            };

            if (!panel) {
                finishClose();
                return;
            }

            if (!animate) {
                setMobilePanelImmediate(panel, false);
                finishClose();
                return;
            }

            animateMobilePanel(panel, false, finishClose);
        }

        function closeAllSubdropdowns(root = nav, animate = true, exceptItem = null) {
            root.querySelectorAll('[data-wambolt-mobile-subdropdown-item]')
                .forEach(item => {
                    if (item === exceptItem) return;
                    closeSubdropdown(item, false, animate);
                });
        }

        function openSubdropdown(item) {
            if (!item) return;

            const subToggle = item.querySelector('[data-wambolt-mobile-subdropdown-toggle]');
            const panel = item.querySelector('[data-wambolt-mobile-subdropdown-panel]');

            if (!subToggle || !panel) return;

            const parentDropdown = item.closest('[data-wambolt-mobile-dropdown-panel]');

            if (parentDropdown) {
                closeAllSubdropdowns(parentDropdown, true, item);
            }

            item.setAttribute('data-open', 'true');
            subToggle.setAttribute('aria-expanded', 'true');
            animateMobilePanel(panel, true);
        }

        function toggleSubdropdown(item) {
            if (!item) return;

            const isExpanded = item.getAttribute('data-open') === 'true';

            if (isExpanded) {
                closeSubdropdown(item, false, true);
                return;
            }

            openSubdropdown(item);
        }

        /* MOBILE TOP-LEVEL DROPDOWNS */

        function closeDropdown(item, restoreFocus = false, animate = true) {
            if (!item) return;

            const dropdownToggle = item.querySelector('[data-wambolt-mobile-dropdown-toggle]');
            const panel = item.querySelector('[data-wambolt-mobile-dropdown-panel]');

            item.setAttribute('data-open', 'false');

            if (dropdownToggle) {
                dropdownToggle.setAttribute('aria-expanded', 'false');
            }

            const finishClose = () => {
                closeAllSubdropdowns(item, false);
                if (restoreFocus && dropdownToggle) dropdownToggle.focus();
            };

            if (!panel) {
                finishClose();
                return;
            }

            if (!animate) {
                setMobilePanelImmediate(panel, false);
                finishClose();
                return;
            }

            animateMobilePanel(panel, false, finishClose);
        }

        function closeAllDropdowns(exceptItem = null, animate = true) {
            dropdownItems.forEach(item => {
                if (item === exceptItem) return;
                if (item.getAttribute('data-open') !== 'true') return;
                closeDropdown(item, false, animate);
            });
        }

        function openDropdown(item) {
            if (!item) return;

            const dropdownToggle = item.querySelector('[data-wambolt-mobile-dropdown-toggle]');
            const panel = item.querySelector('[data-wambolt-mobile-dropdown-panel]');

            if (!dropdownToggle || !panel) return;

            closeAllDropdowns(item, true);

            item.setAttribute('data-open', 'true');
            dropdownToggle.setAttribute('aria-expanded', 'true');
            animateMobilePanel(panel, true);
        }

        function toggleDropdown(item) {
            if (!item) return;

            const isExpanded = item.getAttribute('data-open') === 'true';

            if (isExpanded) {
                closeDropdown(item, false, true);
                return;
            }

            openDropdown(item);
        }

        function resetDropdowns() {
            closeAllDropdowns(null, false);
            closeAllSubdropdowns(nav, false);
            queueMeasureContent();
        }

        /* INITIAL DROPDOWN STATE */

        dropdownItems.forEach(item => {
            item.setAttribute('data-open', 'false');

            const dropdownToggle = item.querySelector('[data-wambolt-mobile-dropdown-toggle]');
            const panel = item.querySelector('[data-wambolt-mobile-dropdown-panel]');

            if (dropdownToggle) dropdownToggle.setAttribute('aria-expanded', 'false');

            if (panel) {
                panel.hidden = true;
                panel.setAttribute('inert', '');
            }
        });

        subdropdownItems.forEach(item => {
            item.setAttribute('data-open', 'false');

            const subToggle = item.querySelector('[data-wambolt-mobile-subdropdown-toggle]');
            const panel = item.querySelector('[data-wambolt-mobile-subdropdown-panel]');

            if (subToggle) subToggle.setAttribute('aria-expanded', 'false');

            if (panel) {
                panel.hidden = true;
                panel.setAttribute('inert', '');
            }
        });

        /* MOBILE DROPDOWN EVENTS */

        const dropdownHandlers = new Map();

        dropdownToggles.forEach(dropdownToggle => {
            const item = dropdownToggle.closest('[data-wambolt-mobile-dropdown-item]');

            const handleDropdownClick = event => {
                event.preventDefault();
                event.stopPropagation();
                toggleDropdown(item);
            };

            const handleDropdownKeydown = event => {
                if (event.key === 'Escape') {
                    const isExpanded = item?.getAttribute('data-open') === 'true';

                    if (isExpanded) {
                        event.preventDefault();
                        event.stopPropagation();
                        closeDropdown(item, true);
                        queueMeasureContent();
                    }
                }
            };

            dropdownToggle.addEventListener('click', handleDropdownClick);
            dropdownToggle.addEventListener('keydown', handleDropdownKeydown);

            dropdownHandlers.set(dropdownToggle, {
                handleDropdownClick,
                handleDropdownKeydown
            });
        });

        const subdropdownHandlers = new Map();

        subdropdownToggles.forEach(subToggle => {
            const item = subToggle.closest('[data-wambolt-mobile-subdropdown-item]');

            const handleSubdropdownClick = event => {
                event.preventDefault();
                event.stopPropagation();
                toggleSubdropdown(item);
            };

            const handleSubdropdownKeydown = event => {
                if (event.key === 'Escape') {
                    const isExpanded = item?.getAttribute('data-open') === 'true';

                    if (isExpanded) {
                        event.preventDefault();
                        event.stopPropagation();
                        closeSubdropdown(item, true);
                        queueMeasureContent();
                    }
                }
            };

            subToggle.addEventListener('click', handleSubdropdownClick);
            subToggle.addEventListener('keydown', handleSubdropdownKeydown);

            subdropdownHandlers.set(subToggle, {
                handleSubdropdownClick,
                handleSubdropdownKeydown
            });
        });

        /* SCROLL LOCK */

        function getLenis() {
            const instance = window.wamboltLenis;

            if (!instance || typeof instance.stop !== 'function') {
                return null;
            }

            return instance;
        }

        function lockPage() {
            if (unlockTimer) {
                window.clearTimeout(unlockTimer);
                unlockTimer = null;
            }

            if (previousDocumentOverflow === null) {
                previousDocumentOverflow = document.documentElement.style.overflow;
            }

            document.documentElement.style.overflow = 'hidden';

            const lenis = getLenis();

            if (lenis && !lenis.isStopped) {
                lenis.stop();
                lenisStoppedByNavigation = true;
            }
        }

        function unlockPage() {
            if (previousDocumentOverflow !== null) {
                document.documentElement.style.overflow = previousDocumentOverflow;
                previousDocumentOverflow = null;
            }

            if (!lenisStoppedByNavigation) return;

            const lenis = window.wamboltLenis;

            if (lenis && typeof lenis.start === 'function') {
                lenis.start();
            }

            lenisStoppedByNavigation = false;
        }

        function scheduleUnlock() {
            if (unlockTimer) {
                window.clearTimeout(unlockTimer);
                unlockTimer = null;
            }

            if (REDUCED_MOTION_QUERY.matches) {
                unlockPage();
                return;
            }

            unlockTimer = window.setTimeout(() => {
                unlockPage();
                unlockTimer = null;
            }, NAV_CLOSE_DURATION);
        }

        function clearCloseResetTimer() {
            if (closeResetTimer === null) return;
            window.clearTimeout(closeResetTimer);
            closeResetTimer = null;
        }

        function scheduleDropdownReset() {
            clearCloseResetTimer();

            const reset = () => {
                resetDropdowns();
                if (inner) inner.scrollTop = 0;
                closeResetTimer = null;
            };

            if (REDUCED_MOTION_QUERY.matches) {
                reset();
                return;
            }

            closeResetTimer = window.setTimeout(reset, NAV_CLOSE_DURATION);
        }

        /* OPEN / CLOSE MOBILE NAVIGATION */

        function openNavigation() {
            if (isOpen()) return;

            measureContent();
            setNavigationState(true);
            lockPage();
            window.requestAnimationFrame(measureContent);
        }

        function closeNavigation() {
            if (!isOpen()) return;

            setNavigationState(false);
            scheduleUnlock();
            scheduleDropdownReset();
        }

        function toggleNavigation() {
            if (isOpen()) {
                closeNavigation();
            } else {
                openNavigation();
            }
        }

        if (toggle) {
            toggle.addEventListener('click', toggleNavigation);
        }

        closeButtons.forEach(button => {
            button.addEventListener('click', closeNavigation);
        });

        navigationLinks.forEach(link => {
            link.addEventListener('click', closeNavigation);
        });

        /* KEYBOARD AND FOCUS TRAP */

        function getFocusableElements() {
            return Array.from(
                nav.querySelectorAll(
                    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
                )
            ).filter(element => {
                if (
                    element.closest('[inert]') ||
                    element.getClientRects().length === 0
                ) {
                    return false;
                }
                return true;
            });
        }

        function handleFocusTrap(event) {
            if (event.key !== 'Tab' || !isOpen()) return;

            const focusableElements = getFocusableElements();
            if (!focusableElements.length) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (
                event.shiftKey &&
                document.activeElement === firstElement
            ) {
                event.preventDefault();
                lastElement.focus();
                return;
            }

            if (
                !event.shiftKey &&
                document.activeElement === lastElement
            ) {
                event.preventDefault();
                firstElement.focus();
            }
        }

        function handleKeydown(event) {
            if (event.key === 'Escape' && isOpen()) {
                event.preventDefault();

                const focusedSubgroup = document.activeElement?.closest?.(
                    '[data-wambolt-mobile-subdropdown-item][data-open="true"]'
                );

                if (focusedSubgroup) {
                    closeSubdropdown(focusedSubgroup, true);
                    queueMeasureContent();
                    return;
                }

                const focusedDropdown = document.activeElement?.closest?.(
                    '[data-wambolt-mobile-dropdown-item][data-open="true"]'
                );

                if (focusedDropdown) {
                    closeDropdown(focusedDropdown, true);
                    queueMeasureContent();
                    return;
                }

                closeNavigation();

                if (toggle) toggle.focus();
                return;
            }

            handleFocusTrap(event);
        }

        document.addEventListener('keydown', handleKeydown);

        /* RESIZE AND BREAKPOINT */

        function handleResize() {
            if (!isOpen()) return;
            queueMeasureContent();
        }

        window.addEventListener('resize', handleResize, { passive: true });

        function handleBreakpoint(event) {
            if (!event.matches) return;

            if (unlockTimer) {
                window.clearTimeout(unlockTimer);
                unlockTimer = null;
            }

            resetDropdowns();
            setNavigationState(false);
            unlockPage();

            if (inner) inner.scrollTop = 0;
        }

        if (typeof DESKTOP_QUERY.addEventListener === 'function') {
            DESKTOP_QUERY.addEventListener('change', handleBreakpoint);
        } else if (typeof DESKTOP_QUERY.addListener === 'function') {
            DESKTOP_QUERY.addListener(handleBreakpoint);
        }

        /* INITIAL STATE */

        if (isOpen()) {
            measureContent();
            setNavigationState(true);
            lockPage();
        } else {
            setNavigationState(false);
        }

        /* MOBILE CLEANUP */

        nav._wamboltNavigationCleanup = () => {
            if (toggle) {
                toggle.removeEventListener('click', toggleNavigation);
            }

            closeButtons.forEach(button => {
                button.removeEventListener('click', closeNavigation);
            });

            navigationLinks.forEach(link => {
                link.removeEventListener('click', closeNavigation);
            });

            dropdownHandlers.forEach((handlers, dropdownToggle) => {
                dropdownToggle.removeEventListener('click', handlers.handleDropdownClick);
                dropdownToggle.removeEventListener('keydown', handlers.handleDropdownKeydown);
            });

            subdropdownHandlers.forEach((handlers, subToggle) => {
                subToggle.removeEventListener('click', handlers.handleSubdropdownClick);
                subToggle.removeEventListener('keydown', handlers.handleSubdropdownKeydown);
            });

            document.removeEventListener('keydown', handleKeydown);
            window.removeEventListener('resize', handleResize);

            if (typeof DESKTOP_QUERY.removeEventListener === 'function') {
                DESKTOP_QUERY.removeEventListener('change', handleBreakpoint);
            } else if (typeof DESKTOP_QUERY.removeListener === 'function') {
                DESKTOP_QUERY.removeListener(handleBreakpoint);
            }

            if (unlockTimer) {
                window.clearTimeout(unlockTimer);
                unlockTimer = null;
            }

            clearCloseResetTimer();

            if (measureFrame !== null) {
                window.cancelAnimationFrame(measureFrame);
                measureFrame = null;
            }

            items.forEach(item => {
                item.style.removeProperty('transition-delay');

                const linkText = item.querySelector('.wambolt-mobile-nav__link-text');

                if (linkText) {
                    linkText.style.removeProperty('transition-delay');
                }
            });

            if (content) {
                content.style.removeProperty('--wambolt-mobile-nav-content-height');
            }

            resetDropdowns();
            setNavigationState(false);
            unlockPage();

            delete nav.dataset.wamboltNavigationInit;
        };
    }

    /* DESTROY */

    function destroyPreorderCTA(header) {
        if (
            header &&
            typeof header._wamboltPreorderCtaCleanup === 'function'
        ) {
            header._wamboltPreorderCtaCleanup();
            delete header._wamboltPreorderCtaCleanup;
        }
    }

    function destroyHeaderScroll(header) {
        if (
            header &&
            typeof header._wamboltScrollCleanup === 'function'
        ) {
            header._wamboltScrollCleanup();
            delete header._wamboltScrollCleanup;
        }
    }

    function destroyDesktopMega(header) {
        if (
            header &&
            typeof header._wamboltDesktopMegaCleanup === 'function'
        ) {
            header._wamboltDesktopMegaCleanup();
            delete header._wamboltDesktopMegaCleanup;
        }
    }

    function destroyNavigation(nav) {
        if (
            nav &&
            typeof nav._wamboltNavigationCleanup === 'function'
        ) {
            nav._wamboltNavigationCleanup();
            delete nav._wamboltNavigationCleanup;
        }
    }

    /* INITIALIZATION */

    function initHeader(header) {
        initHeaderScrollVisibility(header);
        initDesktopMega(header);
        initPreorderCTA(header);
    }

    function initAll(root = document) {
        if (root.matches && root.matches(HEADER_SELECTOR)) {
            initHeader(root);
        }

        root.querySelectorAll(HEADER_SELECTOR).forEach(initHeader);

        if (root.matches && root.matches(NAV_SELECTOR)) {
            initNavigation(root);
        }

        root.querySelectorAll(NAV_SELECTOR).forEach(initNavigation);
    }

    function destroyAll(root = document) {
        if (root.matches && root.matches(HEADER_SELECTOR)) {
            destroyPreorderCTA(root);
            destroyDesktopMega(root);
            destroyHeaderScroll(root);
        }

        root.querySelectorAll(HEADER_SELECTOR).forEach(header => {
            destroyPreorderCTA(header);
            destroyDesktopMega(header);
            destroyHeaderScroll(header);
        });

        if (root.matches && root.matches(NAV_SELECTOR)) {
            destroyNavigation(root);
        }

        root.querySelectorAll(NAV_SELECTOR).forEach(destroyNavigation);
    }

    function init() {
        initAll(document);
    }

    /* INITIAL LOAD */

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }

    /* SHOPIFY THEME EDITOR */

    document.addEventListener('shopify:section:load', event => {
        initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', event => {
        destroyAll(event.target);
    });
})();
